import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

interface SessionResponse {
  token: string;
  expires_at: string;
}

interface BrowserIdentity {
  userId: string;
  revision: string;
}

/** Archive and per-scene tags are limited to Premium (`user_plan` 3) and Enterprise (4). */
export const STAC_ELIGIBLE_PLANS = [3, 4];

/**
 * May this user see ANY STAC surface — the Archive nav entry, the scene tag rows on My
 * Collections, whatever comes next?
 *
 * ONE definition, deliberately, and exported from here rather than owned by either caller: the two
 * surfaces are the same entitlement seen from two pages, and a second copy of `[3, 4]` is a copy
 * that eventually disagrees — a user who can tag scenes but cannot open the Archive those tags
 * exist to be searched in.
 *
 * Both halves matter. `stacUiEnabled` is the per-deployment dark-launch flag (plans/stac-frontend.md
 * F3); with STAC off for a deployment, tagging scenes is typing into a feature nobody can reach.
 * The plan check is client-side ONLY — there is still no server-supplied `planDetails.features` key
 * for STAC, so `hasFeature()` cannot express this and the API does not enforce it.
 */
export function isStacEligible(user: any): boolean {
  return environment.stacUiEnabled === 'true' && STAC_ELIGIBLE_PLANS.includes(Number(user?.user_plan));
}

/** Receive Data: the permission level the Collections page checks (app-layout, RoleGuard). */
export const RECEIVE_DATA = 5;

/**
 * May this user see collection data at all — the Collections page's own rule (app-layout's menu,
 * RoleGuard's `collections` route): a super user, anyone outside a permission-based organisation,
 * or a sub user with Receive Data. Delegate My Tasks alone does not count.
 *
 * A UI gate only, like `isStacEligible`. What the Data Catalog shows is decided on the server from
 * eeuserapi's GET /admin/visible-user-ids (plans/stac-permission-visibility.md), which answers
 * nothing at all for a sub user without Receive Data; this just keeps the menu, the route and the
 * page's STAC controls from offering them a catalog that would be empty.
 */
export function hasReceiveData(user: any): boolean {
  return user?.userType === 'Super user' || !user?.permission_based ||
    (Array.isArray(user?.permissions) && user.permissions.includes(RECEIVE_DATA));
}

/** Both gates: the plan and the deployment (`isStacEligible`), and Receive Data (`hasReceiveData`). */
export function canUseStac(user: any): boolean {
  return isStacEligible(user) && hasReceiveData(user);
}

export function isStacRequest(url: string): boolean {
  const target = new URL(url, window.location.origin);
  const base = new URL(environment.stacApiUrl, window.location.origin);
  return target.origin === base.origin &&
    (target.pathname === base.pathname || target.pathname.startsWith(base.pathname + '/'));
}

@Injectable({ providedIn: 'root' })
export class StacSessionService implements OnDestroy {
  readonly error$ = new BehaviorSubject<string | null>(null);
  readonly accountChanged$ = new Subject<void>();
  /** The side menu's Data Catalog link was pressed: an open catalog goes to its home page. */
  readonly home$ = new Subject<void>();
  private readonly identityKey = 'ee-stac-browser-identity';
  private readonly revisionKey = 'ee-stac-tab-revision';
  private readonly jwt = new JwtHelperService();
  private cached: { token: string; expiry: number; platform: string; revision: string } | null = null;
  private pending: Promise<string> | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private active = false;

  private readonly onStorage = (event: StorageEvent): void => {
      if (event.key === this.identityKey && this.platformToken() &&
          this.identity()?.revision !== sessionStorage.getItem(this.revisionKey)) {
        this.reset();
        this.accountChanged$.next();
      }
  };
  private readonly onVisible = (): void => {
    if (document.visibilityState === 'visible') this.resume();
  };
  private readonly onResume = (): void => this.resume();

  constructor(private http: HttpClient) {
    window.addEventListener('storage', this.onStorage);
    document.addEventListener('visibilitychange', this.onVisible);
    window.addEventListener('online', this.onResume);
    window.addEventListener('pageshow', this.onResume);
  }

  ngOnDestroy(): void {
    this.reset();
    window.removeEventListener('storage', this.onStorage);
    document.removeEventListener('visibilitychange', this.onVisible);
    window.removeEventListener('online', this.onResume);
    window.removeEventListener('pageshow', this.onResume);
  }

  platformToken(): string | null {
    const raw = sessionStorage.getItem('jwttoken');
    if (!raw) return null;
    try {
      const value = JSON.parse(raw);
      return typeof value === 'string' ? value : null;
    } catch {
      return raw;
    }
  }

  login(platform: string): void {
    this.reset();
    const userId = this.claims(platform).id;
    if (typeof userId !== 'string' || !userId) throw new Error('Invalid platform identity.');
    const revision = crypto.randomUUID();
    sessionStorage.setItem(this.revisionKey, revision);
    localStorage.setItem(this.identityKey, JSON.stringify({ userId, revision }));
  }

  async logout(): Promise<void> {
    const platform = this.platformToken();
    const identity = this.identity();
    this.reset();
    if (identity && (!platform || identity.userId !== this.claims(platform).id ||
      identity.revision !== sessionStorage.getItem(this.revisionKey))) return;
    localStorage.removeItem(this.identityKey);
    sessionStorage.removeItem(this.revisionKey);
    await this.lock(async () => {
      if (!this.identity()) await firstValueFrom(this.http.delete(this.sessionUrl()));
    });
  }

  ensure(force = false): Promise<string> {
    const platform = this.platformToken();
    if (!platform) return Promise.reject(new Error('You are not signed in.'));
    const claims = this.claims(platform);
    if (!claims.id || typeof claims.exp !== 'number' || !Number.isFinite(claims.exp) || claims.exp * 1000 <= Date.now()) {
      this.reset();
      this.accountChanged$.next();
      return Promise.reject(new Error('Your platform session has expired.'));
    }
    let identity = this.identity();
    const tabRevision = sessionStorage.getItem(this.revisionKey);
    if (!identity && !tabRevision) {
      this.login(platform);
      identity = this.identity();
    }
    if (!identity || identity.userId !== claims.id || (tabRevision && tabRevision !== identity.revision)) {
      this.reset();
      this.accountChanged$.next();
      return Promise.reject(new Error('The active account changed in another tab.'));
    }
    sessionStorage.setItem(this.revisionKey, identity.revision);
    this.active = true;
    if (!force && this.cached && this.cached.platform === platform && this.cached.revision === identity.revision &&
        this.cached.expiry - Date.now() > 60000) return Promise.resolve(this.cached.token);
    if (this.pending) return this.pending;
    const revision = identity.revision;
    const pending = this.lock(async () => {
      this.assertCurrent(platform, revision);
      const result = await firstValueFrom(this.http.post<SessionResponse>(this.sessionUrl(), {}, {
        headers: { Authorization: `Bearer ${platform}` },
      }));
      try {
        this.assertCurrent(platform, revision);
      } catch (error) {
        await firstValueFrom(this.http.delete(this.sessionUrl()));
        throw error;
      }
      const expiry = Date.parse(result.expires_at);
      if (!result.token || !Number.isFinite(expiry) || expiry <= Date.now()) throw new Error('Invalid STAC session response.');
      this.cached = { token: result.token, expiry, platform, revision };
      this.error$.next(null);
      const remaining = expiry - Date.now();
      this.schedule(Math.max(1000, remaining - Math.min(60000, remaining / 5)));
      return result.token;
    }).catch(error => {
      if (error instanceof HttpErrorResponse && error.status === 401 && this.platformToken() === platform) {
        this.reset();
        this.accountChanged$.next();
      }
      if (this.active && this.platformToken() === platform) {
        this.error$.next('The catalog session could not be renewed. Please retry.');
        this.schedule(15000);
      }
      throw error;
    }).finally(() => {
      if (this.pending === pending) this.pending = null;
    });
    this.pending = pending;
    return pending;
  }

  private resume(): void {
    if (this.active) void this.ensure().catch(() => {});
  }

  private schedule(delay: number): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => { void this.ensure(true).catch(() => {}); }, delay);
  }

  private reset(): void {
    this.active = false;
    this.cached = null;
    this.pending = null;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.error$.next(null);
  }

  private identity(): BrowserIdentity | null {
    try {
      const value = JSON.parse(localStorage.getItem(this.identityKey) || 'null');
      return value && typeof value.userId === 'string' && typeof value.revision === 'string' ? value : null;
    }
    catch { return null; }
  }

  private claims(platform: string): { id?: string; exp?: number } {
    try { return this.jwt.decodeToken(platform) || {}; }
    catch { return {}; }
  }

  private assertCurrent(platform: string, revision: string): void {
    if (this.platformToken() !== platform || this.identity()?.revision !== revision) {
      throw new Error('The platform session changed.');
    }
  }

  private sessionUrl(): string {
    const base = new URL(environment.stacApiUrl, window.location.origin);
    if (base.origin !== window.location.origin) throw new Error('STAC sessions require a same-origin API.');
    return `${environment.stacApiUrl}/session`;
  }

  private lock<Result>(run: () => Promise<Result>): Promise<Result> {
    const locks = (navigator as Navigator & {
      locks?: { request<T>(name: string, callback: () => Promise<T>): Promise<T> };
    }).locks;
    if (!locks) return Promise.reject(new Error('STAC sessions require a browser with Web Locks and HTTPS or localhost.'));
    return locks.request('ee-stac-session', run);
  }
}